import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { logoUrl } from '../data/mock';
import { ArrowRight } from 'lucide-react';

const Hero = () => {
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Tech Background with Hexagons */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-[#0a1e30] via-[#0B2E4B] to-[#0d3a5c]"
      >
        {/* Hexagon Pattern SVG */}
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
              <polygon 
                points="24.8,22 37.3,29.2 37.3,43.4 24.8,50.6 12.3,43.4 12.3,29.2" 
                fill="none" 
                stroke="#28BBC5" 
                strokeWidth="0.5"
                opacity="0.3"
              />
              <polygon 
                points="24.8,-6.9 37.3,0.3 37.3,14.5 24.8,21.7 12.3,14.5 12.3,0.3" 
                fill="none" 
                stroke="#28BBC5" 
                strokeWidth="0.5"
                opacity="0.3"
              />
              <polygon 
                points="0,14.5 12.5,21.7 12.5,35.9 0,43.1 -12.5,35.9 -12.5,21.7" 
                fill="none" 
                stroke="#28BBC5" 
                strokeWidth="0.5"
                opacity="0.3"
              />
              <polygon 
                points="49.6,14.5 62.1,21.7 62.1,35.9 49.6,43.1 37.1,35.9 37.1,21.7" 
                fill="none" 
                stroke="#28BBC5" 
                strokeWidth="0.5"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>

        {/* Glowing Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#28BBC5]/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#28BBC5]/15 rounded-full blur-[80px]" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[#0B2E4B]/50 rounded-full blur-[60px]" />
        
        {/* Light Dots/Stars */}
        <div className="absolute inset-0">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#28BBC5] rounded-full animate-twinkle"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                opacity: Math.random() * 0.5 + 0.2
              }}
            />
          ))}
        </div>

        {/* Hexagon Decorations */}
        <div className="absolute top-20 left-10 w-20 h-20 border border-[#28BBC5]/30 rotate-12" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
        <div className="absolute bottom-40 right-20 w-32 h-32 border border-[#28BBC5]/20 -rotate-12" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
        <div className="absolute top-1/2 left-5 w-16 h-16 bg-[#28BBC5]/10" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
        {/* Logo Grande Centrado */}
        <div className="mb-0">
          <img 
            src={logoUrl} 
            alt="BrightCore PR" 
            className="h-[400px] md:h-[600px] lg:h-[800px] mx-auto object-contain drop-shadow-2xl"
          />
        </div>

        <h1 className="-mt-24 md:-mt-40 lg:-mt-56 text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
          <span className="block">{t.hero.headline.split(' ').slice(0, 3).join(' ')}</span>
          <span className="text-[#28BBC5]">{t.hero.headline.split(' ').slice(3).join(' ')}</span>
        </h1>
        
        <p className="text-base md:text-lg lg:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
          {t.hero.subheadline}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => scrollToSection('contact')}
            className="group flex items-center justify-center gap-3 px-8 py-4 bg-[#28BBC5] text-white font-semibold text-base rounded-md hover:bg-[#22a5ae] transition-all duration-300 min-w-[180px] shadow-lg shadow-[#28BBC5]/30"
          >
            {t.hero.cta1}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          
          <button
            onClick={() => scrollToSection('portfolio')}
            className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#0B2E4B] font-semibold text-base rounded-md hover:bg-gray-100 transition-all duration-300 min-w-[180px]"
          >
            {t.hero.cta2}
          </button>
        </div>
        
        {/* Spacer para separar del contenido de abajo */}
        <div className="mt-8 md:mt-12 lg:mt-20 xl:mt-32"></div>
      </div>
    </section>
  );
};

export default Hero;
